import type {
  ConfigurationRuleGroupDto,
  RuleInstructionDto
} from '@api/machines'
import { RULE_ACTIONS, syntheticMappedArrays } from '@common/utils'

import {
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonTypography
} from '@components/reusable'
import { type DetailedHTMLProps, type HTMLAttributes, useState } from 'react'
import { useTranslation } from 'react-i18next'

const NO_OPTION_SELECTED = '00000000-0000-0000-0000-000000000000'

type ApplyRuleDialogProps = {
  onClose: () => void
  onSave: (groupId: string) => void
  open: boolean
  rule: ConfigurationRuleGroupDto
}

const ApplyRuleDialog = ({
  onClose,
  onSave,
  open,
  rule = {}
}: ApplyRuleDialogProps) => {
  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const { action, actionGroups, conditionGroups } = rule

  const [selectedGroup, setSelectedGroup] = useState('')

  const getOptionLabel = (instructions: RuleInstructionDto[]) => {
    const optionLabel: DetailedHTMLProps<
      HTMLAttributes<HTMLElement>,
      HTMLElement
    >[] &
      string[] = []

    const mappedInstructions = syntheticMappedArrays(instructions)

    for (const [idx, instruction] of mappedInstructions.entries()) {
      optionLabel.push(
        <b key={`apply-rule-dialog-option-${instruction.id}`}>
          {instruction.itemName}
        </b>
      )

      if (idx < instructions.length - 1) {
        optionLabel.push(' and ')
      }
    }

    return (
      <MolsonTypography>
        {optionLabel.map(
          (
            opt: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> &
              string
          ) => opt
        )}
      </MolsonTypography>
    )
  }

  const getDialogHeaderData = () => {
    let dialogTitle = ''
    let dialogSubtitle = ''

    let itemName = ''

    conditionGroups?.[0].forEach((conditionGroup, i) => {
      itemName += conditionGroup.itemName

      if (i < conditionGroups[0].length - 1) {
        itemName += ','
      }

      itemName += ' '
    })

    switch (action) {
      case RULE_ACTIONS.REPLACE: {
        dialogTitle = t('replaceItem')
        dialogSubtitle = t('selectFollowingItems') + itemName
        break
      }
      case RULE_ACTIONS.REQUIRE: {
        dialogTitle = t('requisiteItem')
        dialogSubtitle = t('selectItems') + itemName
        break
      }
      case RULE_ACTIONS.SUGGEST: {
        dialogTitle = t('suggestItem')
        dialogSubtitle = itemName + t('selectFromFollowingItems')
        break
      }
      default:
        break
    }

    return { dialogSubtitle, dialogTitle }
  }

  const { dialogSubtitle, dialogTitle } = getDialogHeaderData()

  return (
    <MolsonDialog fullWidth={true} maxWidth="md" onClose={onClose} open={open}>
      <MolsonDialogHeader
        handleCloseDialog={onClose}
        hasCloseIcon={true}
        subTitle={dialogSubtitle}
        title={dialogTitle}
      />

      <MolsonDialogContent>
        <MolsonRadioGroup
          onChange={e => {
            setSelectedGroup(e.target.value)
          }}
          sx={{
            gap: 2.5
          }}
          value={selectedGroup}
        >
          {actionGroups?.map(actionGroup => {
            return (
              <MolsonRadioButton
                formControlLabelProps={{
                  label:
                    actionGroup.instructions &&
                    getOptionLabel(actionGroup.instructions)
                }}
                radioBtnProps={{
                  value: actionGroup.group
                }}
                key={actionGroup.group}
              />
            )
          })}
          {action === RULE_ACTIONS.SUGGEST && (
            <MolsonRadioButton
              formControlLabelProps={{
                label: (
                  <MolsonTypography>
                    <b>None of the above</b>
                  </MolsonTypography>
                )
              }}
              radioBtnProps={{
                value: NO_OPTION_SELECTED
              }}
              key="none-option-selected"
            />
          )}
        </MolsonRadioGroup>
      </MolsonDialogContent>
      <MolsonDialogActions>
        <MolsonButton color="secondary" onClick={onClose} variant="outlined">
          {t('cancel')}
        </MolsonButton>
        <MolsonButton
          onClick={() => {
            onSave(selectedGroup)
            setSelectedGroup('')
          }}
          disabled={selectedGroup === ''}
          variant="contained"
        >
          {t('confirm')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}

export { ApplyRuleDialog }
